import {
    getPushNotificationConfiguration,
    registerPushNotificationSubscription,
    removePushNotificationSubscription
} from './axiosHelper';

const WORKER_URL = '/push-service-worker.js';

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator?.userAgent || '');
const isStandalone = () => (
    window.matchMedia?.('(display-mode: standalone)').matches === true
    || window.navigator?.standalone === true
);

export const pushCapability = () => {
    if (typeof window === 'undefined' || !window.isSecureContext) return 'unsupported';
    if (isIos() && !isStandalone()) return 'ios-install-required';
    if (
        !('Notification' in window)
        || !('serviceWorker' in navigator)
        || !('PushManager' in window)
    ) return 'unsupported';
    if (window.Notification.permission === 'denied') return 'denied';
    return 'available';
};

const applicationServerKey = (base64Url) => {
    const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
    const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
};

const registration = () => navigator.serviceWorker.register(WORKER_URL, {
    scope: '/',
    updateViaCache: 'none'
});

const serialized = (subscription) => (
    typeof subscription.toJSON === 'function'
        ? subscription.toJSON()
        : subscription
);

export const currentPushState = async () => {
    const capability = pushCapability();
    if (capability !== 'available') return capability;
    const currentRegistration = await navigator.serviceWorker.getRegistration('/');
    const subscription = await currentRegistration?.pushManager?.getSubscription();
    return subscription ? 'enabled' : 'available';
};

const createBrowserSubscription = (worker, publicKey) => worker.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: applicationServerKey(publicKey)
});

const registerWithServer = ({ headers, subscription }) => (
    registerPushNotificationSubscription({
        headers,
        audience: 'client',
        subscription: serialized(subscription)
    })
);

const renewBrowserSubscription = async ({ headers, worker, subscription, publicKey }) => {
    await removePushNotificationSubscription({
        headers,
        endpoint: subscription.endpoint
    }).catch(() => undefined);
    await subscription.unsubscribe();
    const renewed = await createBrowserSubscription(worker, publicKey);
    const response = await registerWithServer({ headers, subscription: renewed });
    if (response?.data?.resubscribeRequired || response?.data?.enabled !== true) {
        throw new Error('PUSH_RESUBSCRIPTION_FAILED');
    }
    return renewed;
};

const subscribeWithGrantedPermission = async ({ headers, repairMissing = false }) => {
    const config = await getPushNotificationConfiguration({ headers });
    if (!config?.data?.enabled || typeof config?.data?.publicKey !== 'string') {
        return 'unavailable';
    }
    const worker = await registration();
    let subscription = await worker.pushManager.getSubscription();
    if (!subscription) {
        subscription = await createBrowserSubscription(worker, config.data.publicKey);
        const response = await registerWithServer({ headers, subscription });
        if (response?.data?.resubscribeRequired || response?.data?.enabled !== true) {
            throw new Error('PUSH_SUBSCRIPTION_FAILED');
        }
        return 'enabled';
    }
    const response = await registerWithServer({ headers, subscription });
    if (response?.data?.resubscribeRequired || (repairMissing && response?.data?.created === true)) {
        subscription = await renewBrowserSubscription({
            headers,
            worker,
            subscription,
            publicKey: config.data.publicKey
        });
    } else if (response?.data?.enabled !== true) {
        throw new Error('PUSH_SUBSCRIPTION_FAILED');
    }
    return 'enabled';
};

export const enablePushNotifications = async ({ headers }) => {
    const capability = pushCapability();
    if (capability !== 'available') return capability;
    let permission = window.Notification.permission;
    if (permission === 'default') permission = await window.Notification.requestPermission();
    if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'available';
    return subscribeWithGrantedPermission({ headers });
};

export const syncExistingPushNotifications = async ({ headers }) => {
    const capability = pushCapability();
    if (capability !== 'available') return capability;
    if (window.Notification.permission !== 'granted') return currentPushState();
    return subscribeWithGrantedPermission({ headers, repairMissing: true });
};

export const disablePushNotifications = async ({ headers, silent = false }) => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return 'available';
    try {
        const worker = await navigator.serviceWorker.getRegistration('/');
        const subscription = await worker?.pushManager?.getSubscription();
        if (!subscription) return 'available';
        const endpoint = subscription.endpoint;
        try {
            await removePushNotificationSubscription({ headers, endpoint });
        } finally {
            await subscription.unsubscribe();
        }
        return 'available';
    } catch (error) {
        if (!silent) throw error;
        return 'error';
    }
};
